import { useEffect, useState } from "react";
import { useAppSelector } from "../../state/store";
import { snakeToCamel } from "../../utilities/flowBuilder.utility";
import { createList } from "../../components/webhook/helpers";
import {
  cartVariables,
  checkoutVariables,
  customerVariables,
  flowVariables,
  orderVariables,
  paymentVariables,
  productVariables,
  sessionVariables,
  storeVariables,
  ticketVariables,
  macroCustomerVariables,
  macroOrderVariables,
  macroStoreVariables,
  macroHelpdeskVariables,
  macroAgetnVariables
} from "./VariableDirectory";
import { NodeSubType } from "../../models/nodeSubType";
import { CustomFieldHelper } from "../../app/action-block/filter/helpers/customFieldHelper";
import {
  FormSubmittedVarGroup,
  SpecialTrigger,
} from "../../constants/customQueryBuilderValues";

export interface VariableListInterfaceV2 {
  displayName: string;
  description: string;
  orderId: number;
  variables: any;
}

export interface Variable {
  description?: string;
  displayName: string;
  actualValue: string;
  variableType: string;
  isAvailable: boolean;
  actualValueEval?: string;
  isSubHeader?: boolean;
  variables?: { [key: string]: Variable };
  needsFetching?: boolean;
}

export interface PayloadConfig {
  fromWebhook?: boolean;
  hide?: boolean;
}

export interface Props {
  payloadConfig?: PayloadConfig;
}

const useVariablesListHookV2 = (props?: Props) => {
  const globalVariableList = useAppSelector(
    (state) => state.globalVariablesState.globalVariablesList
  );
  const customFieldApiResponse = useAppSelector((state) => state.customFieldsState.customFieldApiResponse);
  const aiVariableList = useAppSelector(
    (state) => state.aiVariablesState.aiVariablesList
  );
  const orderVariablesList = useAppSelector(
    (state) => state.orderTemplateVariablesState.orderTemplateVariablesList
  );
  const addressVariablesList = useAppSelector(
    (state) => state.whatsappAddressVariablesState.whatsappAddressVariablesList
  );
  const manifestVariableList = useAppSelector(
    (state) => state.aiVariablesState.manifestVariablesList
  );
  const selectedNode = useAppSelector((state) => state.nodeClickState);
  const [variablesList, setVariablesList] = useState<VariableListInterfaceV2[]>(
    []
  );

  const localVariablesList = useAppSelector(
    (state) => state.localVariablesState.localVariablesList
  );
  const localVariablesListType = useAppSelector(
    (state) => state.localVariablesState.localVariablesListType
  );
  const storeState = useAppSelector((state) => state.storeState);
  const homeState = useAppSelector((state) => state.homeState);
  const payloadVariables: any = useAppSelector(
    (state) => state.payloadVariableState.payload
  );

  const webhookVariables: any = useAppSelector(
    (state) => state.payloadVariableState.webhookPayload
  );

  const externalApiDataVariableList = useAppSelector(
    (state) => state.apiDataState.externalApiData
  );

  const triggers: { [key: string]: any } = useAppSelector(
    (state) => state.automationTriggerState.triggers
  );

  const eventVariables =
    useAppSelector((state) => state.eventPropertyState.data) || {};

  const apiDataVariableList = useAppSelector(
    (state) => state.apiDataState.apiData
  );

  const customApiRegex = /^N\d+ - \d+$/;
  const brandApiRegex = /^N\d+ - \d+ - \w+$/;
  const discountVariableList = useAppSelector(
    (state) => state.apiDataState.discountData
  );

  const getAiVariablesList = () => {
    const payloadData: VariableListInterfaceV2 = {
      displayName: "AI Variables",
      description: "Variables returned by Bik AI blocks",
      orderId: 36,
      variables: {},
    };
    Object.keys(aiVariableList).forEach((k) => {
      const nodeName = aiVariableList[k].name.split("#")[0].trim();
      const payloadVar: any = {
        description: undefined,
        displayName: aiVariableList[k].name,
        actualValue: `{{${aiVariableList[k].value}}}`,
        isAvailable: true,
        isSubHeader: false,
        variableType: aiVariableList[k].type,
      };
      if (nodeName === "AI Ask") {
        payloadVar.isSubHeader = true;
        payloadVar.variables = {};
        processObject(
          aiVariableList[k].payload,
          payloadVar.variables,
          undefined,
          `aiAsk${aiVariableList[k].name.split("#")[1].trim()}`
        );
      }
      if (nodeName === "AI Agent") {
        payloadVar.isSubHeader = true;
        payloadVar.variables = {};
        processObject(
          aiVariableList[k].payload,
          payloadVar.variables,
          undefined,
          `aiAgent${aiVariableList[k].name.split("#")[1].trim()}`
        );
      }
      if (nodeName === "AI VOICE AGENT") {
        payloadVar.isSubHeader = true;
        payloadVar.variables = {};
        processObject(
          aiVariableList[k].payload,
          payloadVar.variables,
          undefined,
          `aiVoiceAgent${aiVariableList[k].name.split("#")[1].trim()}`
        );
      }
      payloadData.variables[k] = payloadVar as Variable;
    });

    const outerKeys = Object.keys(manifestVariableList);
    const nodeKeys = outerKeys.filter(
      (item) => customApiRegex.test(item) || brandApiRegex.test(item)
    );

    nodeKeys.forEach((k) => {
      const nodeIndex = k.split("-")[1].trim();
      const brand = k.split("-")?.[2]?.trim();
      let customVar: any = {
        displayName: `${snakeToCamel(brand)}${nodeIndex}`,
        actualValue: `${snakeToCamel(brand)}${nodeIndex}`,
        variableType: "any",
        isAvailable: true,
        isSubHeader: true,
        variables: {},
      };

      if (
        brand == NodeSubType.MANIFEST_LLM_CALL ||
        brand == NodeSubType.MANIFEST_SEARCH
      ) {
        processObject(
          manifestVariableList[k],
          customVar.variables,
          undefined,
          `${snakeToCamel(brand)}${nodeIndex}`
        );
      } else {
        customVar = {
          description: undefined,
          displayName: `${snakeToCamel(brand)}${nodeIndex}`,
          actualValue: `{{${snakeToCamel(brand)}${nodeIndex}.response}}`,
          variableType: "string",
          isAvailable: true,
        };
      }
      payloadData.variables[`${snakeToCamel(brand)}${nodeIndex}`] = customVar;
    });
    return payloadData;
  };

  const getOrderTemplateVariablesList = () => {
    const payloadData: VariableListInterfaceV2 = {
      displayName: "Whatsapp Payment Variables",
      description: "Variables returned by Whatsapp Payment block",
      orderId: 37,
      variables: {},
    };
    const nodeKeys = Object.keys(orderVariablesList);

    nodeKeys.forEach((k) => {
      const nodeIndex = k.split("-")[1].trim();
      const customVar: any = {
        displayName: `whatsappPayment${nodeIndex}`,
        actualValue: `whatsappPayment${nodeIndex}`,
        variableType: "any",
        isAvailable: true,
        isSubHeader: true,
        variables: {},
      };
      Object.keys(orderVariablesList[k]).forEach((key) => {
        const newCustomVar = {
          displayName: key,
          actualValue: `{{whatsappPayment${nodeIndex}.${key}}}`,
          variableType: orderVariablesList[k][key] || "any",
          isAvailable: true,
          isSubHeader: false,
        };
        customVar.variables[key] = newCustomVar;
      });
      payloadData.variables[`whatsappPayment${nodeIndex}`] = customVar;
    });

    return payloadData;
  };

  const getWhatsappAddressVariablesList = () => {
    const payloadData: VariableListInterfaceV2 = {
      displayName: "Whatsapp Address Variables",
      description: "Variables returned by address block",
      orderId: 38,
      variables: {},
    };
    const nodeKeys = Object.keys(addressVariablesList);

    nodeKeys.forEach((k) => {
      const nodeIndex = k.split("-")[1].trim();
      const customVar: any = {
        displayName: `whatsappAddress${nodeIndex}`,
        actualValue: `whatsappAddress${nodeIndex}`,
        variableType: "any",
        isAvailable: true,
        isSubHeader: true,
        variables: {},
      };
      Object.keys(addressVariablesList[k]).forEach((key) => {
        const newCustomVar = {
          displayName: key,
          actualValue: `{{whatsappAddress${nodeIndex}.${key}}}`,
          variableType: addressVariablesList[k][key] || "any",
          isAvailable: true,
          isSubHeader: false,
        };
        customVar.variables[key] = newCustomVar;
      });
      payloadData.variables[`whatsappAddress${nodeIndex}`] = customVar;
    });

    return payloadData;
  };

  const getGlobalVarList = (listType: string) => {
    const payloadData: VariableListInterfaceV2 = {
      displayName: "Global Variables",
      description:
        "Variables you have defined in the variables section on your dashboard",
      orderId: 40,
      variables: {},
    };

    Object.keys(globalVariableList).forEach((k) => {
      let varType = globalVariableList[k].type || "any";
      if (["Number", "Integer"].includes(varType)) {
        varType = "number";
      } else if (varType === "String") {
        varType = "string";
      } else if (varType === "Boolean") {
        varType = "boolean";
      }

      const payloadVar = {
        description: undefined,
        displayName: k,
        actualValue: listType === "eval" ? `Global['${k}']` : `{{${k}}}`,
        actualValueEval: `Global['${k}']`,
        variableType: varType,
        isAvailable: true,
        isSubHeader: false,
      };
      payloadData.variables[k] = payloadVar as Variable;
    });
    return payloadData;
  };

  const getLocalVarList = (listType: string) => {
    const payloadData: VariableListInterfaceV2 = {
      displayName: "Local User Responses",
      description:
        "Variables containing user responses to questions, lists and QRBs",
      orderId: 32,
      variables: {},
    };

    Object.keys(localVariablesList).forEach((k) => {
      const payloadVar = {
        description: undefined,
        displayName: k,
        actualValue:
          listType === "eval"
            ? `Local['${localVariablesList[k]
              .replace("{{", "")
              .replace("}}", "")}']`
            : `{{${localVariablesList[k]}}}`,
        actualValueEval: `Local['${localVariablesList[k]
          .replace("{{", "")
          .replace("}}", "")}']`,
        variableType:
          localVariablesListType && localVariablesListType[k]
            ? localVariablesListType[k]
            : "string",
        isAvailable: true,
        isSubHeader: false,
      };
      payloadData.variables[k] = payloadVar as Variable;
    });
    return payloadData;
  };

  const getApiDataVarList = (listType: string) => {
    const outerKeys = Object.keys(apiDataVariableList);
    const apiRegex = /^N\d+ - \d+$/;
    const nodeKeys = outerKeys.filter(
      (item) => apiRegex.test(item) || brandApiRegex.test(item)
    );
    const oldKeys = outerKeys.filter((item) => !nodeKeys.includes(item));

    if (oldKeys.length) {
      const payloadData: VariableListInterfaceV2 = {
        displayName: "Api Data Responses",
        description: "API responses received from the APIs",
        orderId: 35,
        variables: {},
      };

      oldKeys.forEach((k) => {
        const payloadVar = {
          description: undefined,
          displayName: k,
          actualValue:
            listType === "eval" ? `response['${k}']` : `{{response.${k}}}`,
          variableType: "any",
          isAvailable: true,
          isSubHeader: false,
        };
        payloadData.variables[k] = payloadVar as Variable;
      });
      return payloadData;
    }

    return {} as VariableListInterfaceV2;
  };

  const getExternalApiVarList = (listType: string) => {
    const payloadData: VariableListInterfaceV2 = {
      displayName: "External Api Responses",
      description: "API responses received when a call external API is used",
      orderId: 34,
      variables: {},
    };

    Object.keys(externalApiDataVariableList).forEach((k) => {
      const payloadVar = {
        description: undefined,
        displayName: k,
        actualValue:
          listType === "eval"
            ? `externalApiResponse['${k}']`
            : `{{externalApiResponse.${k}}}`,
        variableType: "any",
        isAvailable: true,
        isSubHeader: false,
      };
      payloadData.variables[k] = payloadVar as Variable;
    });
    return payloadData;
  };

  const getEventVarList = (
    listType: string,
    eventName: string,
    orderId: number
  ) => {
    const displayName = eventName
      .split(/(?=[A-Z])/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    const headerDescription = eventVariables[eventName]
      ? eventVariables[eventName][0].headerDescription
      : undefined;

    const payloadData: VariableListInterfaceV2 = {
      displayName,
      description: headerDescription,
      orderId: orderId,
      variables: {},
    };


    // Special handling for formSubmitted trigger to categorise variables into Basic and Recommendations based on isFormField property
    if (eventName === `${SpecialTrigger.FormSubmitted}Event`) {
      const basicVars: Record<string, any> = {};
      const recommendationVars: Record<string, any> = {};

      eventVariables[eventName].forEach((event: any) => {
        const payloadVar = {
          description: event.description,
          displayName: event.name,
          actualValue:
            listType === "eval"
              ? `${eventName}['${event.id}']`
              : `{{${eventName}.${event.id}}}`,
          variableType: event?.type || "any",
          isAvailable: true,
          isSubHeader: false,
          needsFetching: event?.needsFetching || false,
        };
        if (event.isFormField) {
          recommendationVars[event.id] = payloadVar;
        } else {
          basicVars[event.id] = payloadVar;
        }
      });

      if (Object.keys(basicVars).length) {
        payloadData.variables[FormSubmittedVarGroup.Basic] = {
          isSubHeader: true,
          isAvailable: true,
          displayName: FormSubmittedVarGroup.Basic,
          description: "",
          variables: basicVars,
        } as any;
      }

      // Always add the hardcoded tabularValues variable under Recommendations
      recommendationVars["tabularValues"] = {
        description: "",
        displayName: "Tabular Values",
        actualValue:
          listType === "eval"
            ? `${eventName}['tabularValues']`
            : `{{${eventName}.tabularValues}}`,
        variableType: "string",
        isAvailable: true,
        isSubHeader: false,
        needsFetching: false,
      };

      payloadData.variables[FormSubmittedVarGroup.Recommendations] = {
        isSubHeader: true,
        isAvailable: true,
        displayName: FormSubmittedVarGroup.Recommendations,
        description: "Specific fields from the submitted form",
        variables: recommendationVars,
      } as any;

      return payloadData;
    }

    eventVariables[eventName].forEach((event: any) => {
      const payloadVar = {
        description: event.description,
        displayName: event.name,
        actualValue:
          listType === "eval"
            ? `${eventName}['${event.id}']`
            : `{{${eventName}.${event.id}}}`,
        variableType: event?.type || "any",
        isAvailable: true,
        isSubHeader: false,
        needsFetching: event?.needsFetching || false,
      };
      payloadData.variables[event.id] = payloadVar as Variable;
    });
    return payloadData;
  };

  const addCustomFieldsToCustomerVariables = () => {

    if (!Object.keys(customFieldApiResponse).length) {
      return customerVariables
    }

    const customFieldHelper = new CustomFieldHelper();

    const modifiedCustomVariables = customFieldApiResponse.filter(customField => customField.aliasName).map(customField => {
      return {
        description: undefined,
        displayName: customField.fieldName,
        actualValue: `{{customer.custom.${customField.aliasName}}}`,
        variableType: customFieldHelper.getDataTypeForVariable(customField),
        isAvailable: true,
        isSubHeader: false,
        needsFetching: customField.options?.length > 0,
      }
    })

    const customerVariablesWithCustomFields = {
      ...customerVariables,
      variables: {
        ...customerVariables.variables,
        Custom: {
          description: "",
          displayName: "Custom Fields",
          isAvailable: true,
          isSubHeader: true,
          variables: modifiedCustomVariables,
        }
      },
    };

    return customerVariablesWithCustomFields;
  };

  const applyMacroChannelRecommendations = (custVariables: any, channelToUse: string) => {
    // Deep clone to avoid mutating the original
    const variables = JSON.parse(JSON.stringify(custVariables));
    
    if (variables.variables?.Basic?.variables) {
      // Set Customer email ID and phone variables as available for ticket form macro blocks
      const isTicketFormNode = selectedNode?.nodeSubType === NodeSubType.TICKET_FORM;
      if ((channelToUse === "email" || isTicketFormNode) && variables.variables.Basic.variables["Customer email ID"]) {
        variables.variables.Basic.variables["Customer email ID"].isAvailable = true;
      }
      if (isTicketFormNode && variables.variables.Basic.variables["Customer phoneNumber"]) {
        variables.variables.Basic.variables["Customer phoneNumber"].isAvailable = true;
      }
      
      // Set Customer Instagram username as available for instagram channel
      if (channelToUse === "instagram" && variables.variables.Basic.variables["Customer Instagram username"]) {
        variables.variables.Basic.variables["Customer Instagram username"].isAvailable = true;
      }
    }
    
    return variables;
  };

  const addCustomFieldsToMacroCustomerVariables = () => {
    // Get channel from nodeClickState (per-node channel) or fall back to homeState channel
    const macroChannel = selectedNode?.automationChannel || "whatsapp"; 
    if (!Object.keys(customFieldApiResponse).length) {
      return applyMacroChannelRecommendations(macroCustomerVariables, macroChannel);
    }

    const customFieldHelper = new CustomFieldHelper();

    const modifiedCustomVariables = customFieldApiResponse.filter(customField => customField.aliasName).map(customField => {
      return {
        description: undefined,
        displayName: customField.fieldName,
        actualValue: `{{customer.custom.${customField.aliasName}}}`,
        variableType: customFieldHelper.getDataTypeForVariable(customField),
        isAvailable: true,
        isSubHeader: false,
        needsFetching: customField.options?.length > 0,
      }
    });

    const macroCustomerVariablesWithCustomFields = {
      ...macroCustomerVariables,
      variables: {
        ...macroCustomerVariables.variables,
        Custom: {
          description: "",
          displayName: "Custom Fields",
          isAvailable: true,
          isSubHeader: true,
          variables: modifiedCustomVariables,
        }
      },
    };

    return applyMacroChannelRecommendations(macroCustomerVariablesWithCustomFields, macroChannel);
  };

  const formatVariableList = (
    listType: string,
    varType: string,
    groupName?: string,
    orderId?: number
  ): VariableListInterfaceV2 => {
    if (varType === "global") {
      return getGlobalVarList(listType);
    } else if (varType === "local") {
      return getLocalVarList(listType);
    } else if (varType === "externalApi") {
      return getExternalApiVarList(listType);
    } else if (groupName && groupName === "events-trigger") {
      return getEventVarList(listType, varType, orderId ?? 1);
    } else {
      return getApiDataVarList(listType);
    }
  };

  function processObject(
    obj: any,
    targetObj: any,
    parentKey?: string,
    objName?: string
  ) {
    // Special handling for when the root object is an array
    if (Array.isArray(obj) && !parentKey) {
      obj.forEach((item: any, index: number) => {
        const itemPayloadVar = {
          displayName: index.toString(),
          actualValue: `{{${objName}[${index}]}}`,
          variableType: "any",
          isAvailable: true,
          isSubHeader: false,
        };
        targetObj[index] = itemPayloadVar;
        if (typeof item === "object" && !Array.isArray(item)) {
          targetObj[index].isSubHeader = true;
          targetObj[index].variables = {};
          processObject(
            item,
            targetObj[index].variables,
            `[${index}]`,
            objName
          );
        }
      });
      return;
    }

    for (const key in obj) {
      const currentKey = parentKey ? `${parentKey}.${key}` : key;
      // Handle bracket notation correctly - don't add dot after objName when currentKey starts with bracket
      const currentValue = currentKey.startsWith('[') 
        ? `{{${objName}${currentKey}}}` 
        : `{{${objName}.${currentKey}}}`;
      if (typeof obj[key] !== "object") {
        const payloadVar = {
          displayName: key,
          actualValue: currentValue,
          variableType: obj[key],
          isAvailable: true,
          isSubHeader: false,
        };
        targetObj[key] = payloadVar;
      } else if (Array.isArray(obj[key])) {
        // Handle arrays (recursively)
        const arrayActualValue = currentKey.startsWith('[') 
          ? `{{${objName}${parentKey}.${key}}}` 
          : `{{${objName}.${key}}}`;
        const newPayloadVar: any = {
          displayName: key,
          actualValue: arrayActualValue,
          variableType: "any",
          isAvailable: true,
          isSubHeader: true,
          variables: {},
        };

        targetObj[key] = [];
        newPayloadVar.variables = {};
        obj[key].forEach((item: any, index: number) => {
          const itemActualValue = currentKey.startsWith('[') 
            ? `{{${objName}${parentKey}.${key}[${index}]}}` 
            : `{{${objName}.${currentKey}[${index}]}}`;
          const itemPayloadVar = {
            displayName: index.toString(),
            actualValue: itemActualValue,
            variableType: "any",
            isAvailable: true,
            isSubHeader: false,
          };
          newPayloadVar.variables[index] = itemPayloadVar;
          if (typeof item === "object") {
            newPayloadVar.variables[index].isSubHeader = true;
            newPayloadVar.variables[index].variables = {};
            processObject(
              item,
              newPayloadVar.variables[index].variables,
              `${currentKey}[${index}]`,
              objName
            );
          }
        });
        targetObj[key] = newPayloadVar;
      } else {
        // Handle nested objects (recursively)
        const nestedActualValue = currentKey.startsWith('[') 
          ? `{{${objName}${parentKey}.${key}}}` 
          : `{{${objName}.${key}}}`;
        const newPayloadVar = {
          displayName: key,
          actualValue: nestedActualValue,
          variableType: "any",
          isAvailable: true,
          isSubHeader: true,
          variables: {},
        };

        targetObj[key] = {};
        processObject(obj[key], newPayloadVar.variables, currentKey, objName);
        targetObj[key] = newPayloadVar;
      }
    }
  }

  const getPayloadVariableList = () => {
    const payloadData: VariableListInterfaceV2 = {
      displayName: "Payload Variables",
      description:
        props?.payloadConfig?.fromWebhook ||
          (triggers.length === 1 && triggers[0]?.type === "webhook")
          ? "These are variables passed from the payload received"
          : "These are variables passed from another flow via Start a Flow Block",
      orderId: 30,
      variables: {},
    };

    if (Object.keys(webhookVariables).length) {
      payloadData.variables = createList(webhookVariables, "payload");
    } else {
      processObject(
        payloadVariables,
        payloadData.variables,
        undefined,
        "payload"
      );
    }
    return payloadData;
  };

  const getDiscountVariables = () => {
    const payloadData: VariableListInterfaceV2 = {
      displayName: "Discount Variables",
      description: "",
      orderId: 38,
      variables: {},
    };
    const nodeKeys = Object.keys(discountVariableList);

    nodeKeys.forEach((k) => {
      const nodeIndex = k.split("-")[1].trim();
      const customVar: any = {
        displayName: `discount${nodeIndex}`,
        actualValue: `discount${nodeIndex}`,
        variableType: "any",
        isAvailable: true,
        isSubHeader: true,
        variables: {},
      };
      Object.keys(discountVariableList[k]).forEach((key) => {
        const newCustomVar = {
          displayName: key,
          actualValue: `{{discount${nodeIndex}.${key}}}`,
          variableType: discountVariableList[k][key] || "any",
          isAvailable: true,
          isSubHeader: false,
        };
        customVar.variables[key] = newCustomVar;
      });
      payloadData.variables[`discount${nodeIndex}`] = customVar;
    });

    return payloadData;
  };

  const getCallCustomApiVariables = () => {
    const payloadData: VariableListInterfaceV2 = {
      displayName: "Api response variables",
      description: "API responses received when a call custom API is used",
      orderId: 33,
      variables: {},
    };

    const outerKeys = Object.keys(apiDataVariableList);
    const nodeKeys = outerKeys.filter(
      (item) => customApiRegex.test(item) || brandApiRegex.test(item)
    );

    nodeKeys.forEach((k) => {
      const nodeIndex = k.split("-")[1].trim();
      const brand = k.split("-")?.[2]?.trim();
      const customVar: any = {
        displayName: brand
          ? `${snakeToCamel(brand)}${nodeIndex}`
          : `callCustomApi${nodeIndex}`,
        actualValue: brand
          ? `${snakeToCamel(brand)}${nodeIndex}`
          : `callCustomApi${nodeIndex}`,
        variableType: "any",
        isAvailable: true,
        isSubHeader: true,
        variables: {},
      };

      const brandName = snakeToCamel(brand || "").toLowerCase();

      if (brand && ["restapi"].includes(brandName)) {
        processObject(
          apiDataVariableList[k],
          customVar.variables,
          undefined,
          `${snakeToCamel(brand)}${nodeIndex}`
        );
      } else {
        Object.keys(apiDataVariableList[k]).forEach((key) => {
          const newCustomVar = {
            displayName: key,
            actualValue: brand
              ? `{{${snakeToCamel(brand)}${nodeIndex}.${key}}}`
              : `{{callCustomApi${nodeIndex}.${key}}}`,
            variableType: apiDataVariableList[k][key]?.dataType || "any",
            isAvailable: true,
            isSubHeader: false,
          };
          customVar.variables[key] = newCustomVar;
        });
      }
      payloadData.variables[
        brand
          ? `${snakeToCamel(brand)}${nodeIndex}`
          : `callCustomApi${nodeIndex}`
      ] = customVar;
    });

    return payloadData;
  };

  useEffect(() => {
    if (selectedNode?.nodeId) {
      constructVariableList();
    }
  }, [selectedNode?.nodeId]);

  const constructVariableList = async () => {
    let variableList: VariableListInterfaceV2[] = [];

    const isMacroBuilder = !!homeState?.isMacro;

    if (isMacroBuilder) {
      const macroCustomerVariablesWithCustomFields = addCustomFieldsToMacroCustomerVariables();

      variableList.push(macroStoreVariables);
      variableList.push(macroCustomerVariablesWithCustomFields);
      variableList.push(macroHelpdeskVariables);
      variableList.push(macroOrderVariables);
      variableList.push(macroAgetnVariables);

      variableList.sort((a, b) => a.orderId - b.orderId);
      setVariablesList(variableList);
      return variableList;
    }

    const customerVariablesWithCustomFields = addCustomFieldsToCustomerVariables();

    variableList.push(storeVariables);
    variableList.push(customerVariablesWithCustomFields);
    variableList.push(sessionVariables);
    variableList.push(ticketVariables);
    variableList.push(cartVariables);
    variableList.push(orderVariables);
    variableList.push(productVariables);
    variableList.push(paymentVariables);
    variableList.push(checkoutVariables);
    variableList.push(flowVariables);

    if (Object.keys(globalVariableList).length) {
      variableList.push(formatVariableList("normal", "global"));
    }
    if (Object.keys(localVariablesList).length) {
      variableList.push(formatVariableList("normal", "local"));
    }

    if (
      Object.keys(aiVariableList).length ||
      Object.keys(manifestVariableList).length
    ) {
      variableList.push(getAiVariablesList());
    }

    if (Object.keys(orderVariablesList).length) {
      variableList.push(getOrderTemplateVariablesList());
    }

    if (Object.keys(addressVariablesList).length) {
      variableList.push(getWhatsappAddressVariablesList());
    }

    const outerKeys = Object.keys(apiDataVariableList);
    const apiRegex = /^N\d+ - \d+$/;
    const brandApiRegex = /^N\d+ - \d+ - \w+$/;
    const nodeKeys = outerKeys.filter(
      (item) => apiRegex.test(item) || brandApiRegex.test(item)
    );
    const oldKeys = outerKeys.filter((item) => !nodeKeys.includes(item));
    if (oldKeys.length) {
      variableList.push(formatVariableList("normal", "response"));
    }
    if (Object.keys(externalApiDataVariableList).length) {
      variableList.push(formatVariableList("normal", "externalApi"));
    }
    if (Object.keys(eventVariables).length) {
      let orderId = 11;
      for (const key of Object.keys(eventVariables)) {
        variableList.push(
          formatVariableList("normal", key, "events-trigger", orderId)
        );
        orderId += 2;
      }
    }
    if (
      Object.keys(payloadVariables).length ||
      Object.keys(webhookVariables).length
    ) {
      if (props?.payloadConfig?.hide === true) {
      } else {
        variableList.push(getPayloadVariableList());
      }
    }

    if (nodeKeys && nodeKeys.length > 0) {
      variableList.push(getCallCustomApiVariables());
    }

    if (Object.keys(discountVariableList).length) {
      variableList.push(getDiscountVariables());
    }

    variableList.sort((a, b) => a.orderId - b.orderId);
    setVariablesList(variableList);
    return variableList;
  };

  return {
    variablesList,
    setVariablesList,
    constructVariableList,
  };
};

export default useVariablesListHookV2;
